
import { ScriptAPI } from './ScriptAPI';
import { Command } from './Command';
import { app } from '../app';
import { EntityStatic, EntityAnim } from '../map/Entity';
import { Sprite } from 'pixi.js';

export class Thread {
    constructor(id, runner, shouldHalt) {
        this.id = id;
        this.runner = runner;
        this.context = this;

        this.queue = [];
        this.stack = [];

        this.shouldHalt = shouldHalt;

        this.isPaused = false;
        this.isHalted = false;
        this._isRunning = false;

        this.terminated = false;
    }

    get isFinished() {
        return this.queue.length <= 0 && (!this.shouldHalt || this.runner.halts.length <= 0);
    }

    resumeIfNoHalt() {
        if (this.shouldHalt && this.runner.halts.length <= 0) this.run();
        else this.runner.onHalt(this);
    }

    resume() {
        this.run();
    }

    run() {
        this._isRunning = true;
        while (this._isRunning) {
            if (this.queue.length <= 0) break;

            this.nextCommand = this.queue.shift();
            console.log(this.id + " length >>> " + this.queue.length);
            this._isRunning = this.execute(this.nextCommand);
        }

        if (this.runner.shouldTerminate() && !this.runner.terminated) {
            this.runner.terminate();
            return;
        }

        this.runner.onHalt(this);
    }

    execute(command) {
        if (command instanceof Command) return command.execute(this.context);
        else this.runAPIFunc(command.func, command.args);
    }

    pushToStack(scopeId) {
        let objective = this.runner.findObjectiveById(scopeId);
        if (!objective && this.runner.finishedObjectives.has(scopeId)) {
            objective = this.runner.finishedObjectives.get(scopeId);
        }

        if (objective) this.stack.push(objective.scope);
    }

    popFromStack(scopeId) {
        this.stack.pop();
    }

    runAPIFunc(func, ...args) {
        console.log('Thread ' + this.id + ' >>> ' + func.name + '(' + args.join(',') + ')');
        func.apply(this.context, args);
    }

    findEntity(entityId) {
        let entity = null;
        if (entityId === 'player') entity = this.runner.player;
        if (!entity) entity = this.checkIdOnStack(entityId);
        if (!entity) entity = this.runner.findEntity(entityId);

        return entity;
    }

    checkIdOnStack(id) {
        if (id === 'interact' && this.stack[this.stack.length - 1].interact) return this.stack[this.stack.length - 1].interact;
    }

    terminate() {
        console.log('Terminating thread ' + this.id);
        this.runner.threads.delete(this.id);
    }
}

export class ScriptRunner {
    static processScript(runner, script) {
        let blocks = new Map();
        let entry = [];

        if (!script || !script.entry) {
            console.warn('Mission appears to have no script.');
            return false;
        }

        if (script.blocks) {
            for (const key in script.blocks) {
                if (Object.prototype.hasOwnProperty.call(script.blocks, key)) {
                    blocks.set(key, ScriptRunner.processBlock(script.blocks[key]));
                }
            }
        }

        entry = this.processBlock(script.entry);

        return new ScriptRunner(runner, script.assets, blocks, entry);
    }

    static processBlock(commands) {
        let block = [];

        commands.forEach(command => {
            block.push(Command.fromString(command));
        });

        return block;
    }

    constructor(runner, assets, blocks, entry) {
        this.runner = runner;
        this.worldUI = this.runner.worldUI;
        this.tilemap = this.runner.tilemap;
        this.camera = this.runner.camera;
        this.player = this.runner.player;
        this.aiController = this.runner.aiController;

        this.animExtras = this.runner.neededExtras;

        this.scriptAPI = ScriptAPI;

        this.assets = assets;
        this.blocks = blocks;
        this.entry = entry;

        // Objectives
        this.objectives = [];
        this.finishedObjectives = new Map();
        this.currObjective = null;

        // Place for commands to leave data for future commands.
        this.sharedData = {};

        this.lastEntity = null;

        this.entities = new Map();

        this.mainThread = new Thread('main', this, true);
        this.mainThread.queue = entry;

        this.threads = new Map();
        this.threads.set(this.mainThread.id, this.mainThread);

        this.halts = [];

        this.isRunning = false;
    }

    get allowInteraction() {
        // Block interaction if the runner hasn't started yet.
        let allowInteraction = this.isRunning;

        // If any script in any thread is running then block interaction.
        this.threads.forEach((thread, key) => {
            if (thread.isRunning) allowInteraction = false;
        });

        return allowInteraction;
    }

    run() {
        this.isRunning = true;
        this.mainThread.run();
    }

    checkInteractionAt(tilePos) {
        if (!this.allowInteraction) return;

        this.lastInteractPos = tilePos;
        this.interactWithObjectivesAt(tilePos);
    }

    onHalt(thread) {
        this.getNewObjective();
    }

    runOnThread(id, scopeId, blockId, shouldHalt) {
        if (this.blocks.has(blockId)) {
            let queue = [];
            if (scopeId) queue.unshift(Command.fromString('popFromStack(' + scopeId + ')'));
            queue.unshift.apply(queue, this.blocks.get(blockId));
            if (scopeId) queue.unshift(Command.fromString('pushToStack(' + scopeId + ')'));

            let newThread = new Thread(blockId + '@' + Math.floor(Math.random() * 999999999) + 1, this, false);
            newThread.queue = queue;
            this.threads.set(newThread.id, newThread);
            newThread.run();
        } else if (blockId !== null && blockId !== 'null') {
            console.warn('Failed to find block ' + blockId + '.');
        }
    }

    runCommandsOnThread(commands) {
        let newThread = new Thread('nonBlock@' + Math.floor(Math.random() * 999999999) + 1, this, false);
        newThread.queue = commands;
        this.threads.set(newThread.id, newThread);
        newThread.run();
    }

    shouldTerminate() {
        console.log('ShouldTerminate? ' + (this.objectives.length <= 0 && this.mainThread.isFinished));
        return this.objectives.length <= 0 && this.mainThread.isFinished;
    }

    terminate() {
        this.worldUI.hideObjectiveArrow();
        console.log('Terminating Runner.');
        this.isRunning = false;
        this.terminated = true;
        this.runner.endMission();
    }

    replaceSpecials(input) {
        if (input === 'lastX') return this.lastEntity.tilePos.x;
        if (input === 'lastY') return this.lastEntity.tilePos.y;

        return input;
    }

    getNewObjective() {
        if (this.objectives.length > 0) this.currObjective = this.objectives[0];
    }

    findObjectiveById(objectiveId) {
        return this.objectives.find((objective) => objectiveId === objective.id);
    }

    interactWithObjectivesAt(tilePos) {
        this.entities.forEach((entity, key) => {
            let diffX = tilePos.x - entity.tilePos.x;
            let diffY = tilePos.y - entity.tilePos.y;

            if (Math.abs(diffX) <= 1 && Math.abs(diffY) <= 1) {
                let objective = this.findObjectiveById(entity.id);
                if (objective && objective.entity.id === entity.id) {
                    this.lastEntity = entity;
                    this.completeObjective(objective);
                }
            }
        });

        this.mainThread.resumeIfNoHalt();
    }

    /**
     *
     * @param {Objective} objective
     */
    completeObjective(objective) {
        this.halts = this.halts.filter((halt) => objective.id !== halt);
        this.objectives = this.objectives.filter((arrObjective) => objective.id !== arrObjective.id);
        objective.scope = { interact: objective.entity };
        this.finishedObjectives.set(objective.id, objective);
        //this.runAPIFunc(this.scriptAPI.executeBlock, objective.blockId, objective.id);
        this.runOnThread(objective.id, objective.id, objective.blockId, false);

        //temp fix for objective arrow
        if (this.objectives[0]) {
            this.runner.worldUI.updateObjectiveArrow(this.objectives[0]);
        }
    }

    addObjective(objective) {
        this.objectives.push(objective);
        this.runner.worldUI.updateObjectiveArrow(this.objectives[0]);
    }

    /**
     *
     * @param {string} entityId
     * @returns {EntityStatic|EntityAnim|Boolean}
     */
    findEntity(entityId) {
        if (this.entities.has(entityId)) {
            return this.entities.get(entityId);
        }

        return false;
    }

    deleteEntity(entity) {
        if (!entity) {
            console.warn('Failed to delete an entity');
            return;
        }

        this.tilemap.removeEntity(entity);
        entity.destroy();
        this.entities.delete(entity.id);
    }

    panCameraToTileXY(tileX, tileY, cb) {
        let worldPos = this.tilemap.localToWorldXY(tileX, tileY);
        this.panCameraToXY(worldPos, cb);
    }

    glanceAt(xy, waitTime, cb) {
        // Stop following the character.
        this.camera.pausePlugin('follow');

        this.panCameraToXY(xy, waitTime, () => {
            app.tween(this).wait(waitTime).call(() => {
                this.panCameraToXY(this.player.position, 0, () => {
                    this.camera.resumePlugin('follow');
                    if (cb) cb();
                });
            });

        });
    }

    panCameraToXY(xy, waitTime, cb) {
        this.camera.once('snap-end', () => { cb(); });
        this.camera.snap(xy.x, xy.y, { removeOnComplete: true, removeOnInterrupt: false, interrupt: false, topLeft: false });
        //this.camera.snapZoom({ width: zoom, center: xy, removeOnComplete: true, removeOnInterrupt: true});
    }

    injectSpeech(entity, bubbleTexId) {
        let bubble = new Sprite.from(bubbleTexId);
        bubble.anchor.set(0.5, 1);
        bubble.isTalking = false;

        bubble.reset = () => {
            bubble.alpha = 0;
            bubble.scale.y = 0;
            bubble.position.copyFrom(entity.position);
            bubble.x += entity.width * 0.5;
            bubble.y -= entity.height;
            bubble.targetY = entity.y - entity.height - bubble.height;
            bubble.visible = true;
            bubble.isShown = false;
        };

        bubble.reset();

        bubble.open = (shouldReopen, cb) => {
            if (!shouldReopen && bubble.isShown) {
                if (cb) cb();
                return;
            }

            if (bubble.isTalking) bubble.removeChildren();

            if (bubble.isShown) {
                bubble.hide(() => {
                    app.tween(bubble)
                        .wait(100)
                        .call(() => {
                            bubble.show(cb);
                        });
                });
            } else {
                bubble.show(cb);
            }
        }

        bubble.show = (cb) => {
            bubble.reset();

            // Play sound effect.
            app.sound.play("07-ingame-interaction");

            app.tween(bubble.scale).to({ y: 1 }, 250);
            app.tween(bubble).to({ y: bubble.targetY }, 250);
            app.tween(bubble)
                .to({ alpha: 1 }, 250)
                .call(() => {
                    bubble.isShown = true;
                    if (cb) cb();
                });
        };

        bubble.setSpeech = (bubbleIcon, cb) => {
            bubble.isTalking = true;

            bubble.removeChildren();
            bubble.addChild(bubbleIcon);
            app.tween(bubbleIcon)
                .wait(500)
                .to({ alpha: 1 }, 0)
                .call(() => {
                    if (cb) cb();
                });
        }

        bubble.hide = (cb) => {
            app.tween(bubble)
                .to({ alpha: 0 }, 100)
                .call(() => {
                    bubble.visible = false;
                    bubble.isShown = false;
                    bubble.isTalking = false;
                    bubble.removeChildren();
                    if (cb) cb();
                });
        }

        bubble.say = (bubbleIcon, showTime, waitTime, cb) => {
            bubble.open(true, () => {
                app.tween(bubble)
                    .wait(750 + showTime)
                    .call(() => {
                        bubble.hide(() => {
                            app.tween(bubble)
                                .wait(waitTime)
                                .call(() => {
                                    bubble.visible = false;
                                    if (cb) cb();
                                });
                        });
                    });

                bubble.setSpeech(bubbleIcon);
            });
        };

        this.worldUI.addBubble(bubble);
        bubble.destroy = () => {
            this.worldUI.removeBubble(bubble);
        }

        entity.bubble = bubble;
    }

    speakPermanent(entity, bubbleTexId, bubbleIconTexId, cb) {
        // Check if the entity already has a bubble.
        if (!entity.bubble) this.injectSpeech(entity, bubbleTexId);

        let bubbleIcon = new Sprite.from(bubbleIconTexId);
        bubbleIcon.anchor.set(0.5, 1);
        bubbleIcon.alpha = 0;

        entity.bubble.open(true, () => {
            entity.bubble.setSpeech(bubbleIcon, () => {
                if (cb) cb();
            });
        });
    }

    speakClose(entity, cb) {
        console.log("Inside spript runner");
        if (!entity.bubble) {
            if (cb) cb();
            return;
        }

        entity.bubble.hide(() => {
            console.log("Hiding");
            if (cb) cb();
        });
    }

    speak(entity, bubbleTexId, bubbleIconTexId, showTime, waitTime, cb) {
        // Check if the entity already has a bubble.
        if (!entity.bubble) this.injectSpeech(entity, bubbleTexId);

        let bubbleIcon = new Sprite.from(bubbleIconTexId);
        bubbleIcon.anchor.set(0.5, 1);
        bubbleIcon.alpha = 0;

        entity.bubble.say(bubbleIcon, showTime, waitTime, cb);
    }
}
